<template>
  <div class="px-1">
    <div class="_grid bg-yello1w-50 gap-1">
      <a-input
        :value="getSourceLabel(compareParam.whenSourceKey ?? '')"
        class="bg-[#2E2E2E] w-full"
        placeholder="数据源"
        :disabled="disabled"
        @click.stop="() => onOpenSource('whenSourceKey')"
      />

      <a-select
        :options="operatorOptions"
        :value="compareParam.whenOperator"
        class="w-full"
        placeholder="请选择"
        :disabled="disabled"
        @change="(val: string)=>updateAttrs(compareParam.id, {whenOperator:val as 'Equal'})"
      />
      <a-input-number
        :value="
          isWhenValueSourceKey
            ? getSourceLabel(compareParam.whenValue + '')
            : compareParam.whenValue
        "
        placeholder="数值"
        class="bg-[#2E2E2E] w-full"
        :readonly="isWhenValueSourceKey"
        :disabled="disabled"
        :precision="whenSourceItem?.precision ?? 0"
        :min="whenSourceItem?.minValue ?? -99999"
        :max="maxValue"
        @click="() => isWhenValueSourceKey && onOpenSource('whenValue')"
        @change="(whenValue: number)=>updateAttrs(compareParam.id, {whenValue})"
      >
        <template #addonAfter>
          <icon
            class="mx-1 cursor-pointer opacity-70"
            :class="{}"
            :icon="isWhenValueSourceKey ? 'subtract.svg' : 'add.svg'"
            :width="12"
            :height="12"
            fill="none"
            @click.stop="
              () =>
                !disabled
                  ? isWhenValueSourceKey
                    ? delSourceKey()
                    : onOpenSource('whenValue')
                  : null
            "
          />
        </template>
      </a-input-number>
      <!-- 删除按钮 -->
      <div class="flex items-center justify-between px-2">
        <icon
          class="cursor-pointer opacity-70"
          icon="delete2.svg"
          :width="12"
          :height="12"
          fill="none"
          @click="() => !disabled && $emit('delete', compareParam.id)"
        />
        <icon
          v-if="addable"
          class="cursor-pointer opacity-70 ml-1"
          icon="add.svg"
          :width="12"
          :height="12"
          fill="none"
          @click="() => !disabled && $emit('add', compareId)"
        />
      </div>
    </div>
    <modal-data-source
      ref="sourceModal"
      disable-string
      :source-key="compareParam.whenSourceKey"
      :source-tags="['normal', 'subunit_1']"
      :hidden-fake-item="true"
      :show-hidden-item="true"
      :change-time-comment="true"
      @select="onSourceSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { dataSourceWholeItems } from '@/resources/data-source';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { isSourceKeyFormat } from '@/utils/number';
import { computed, ref } from 'vue';

const props = defineProps<{
  compareParam: CompareParam;
  compareId: string;
  addable: boolean;
  disabled: boolean;
}>();

const runtimeStore = useRuntimeStore();

const operatorOptions = computed(() => {
  return [
    { label: '<', value: 'LessThan' },
    { label: '<=', value: 'LessThanOrEqual' },
    {
      label: '=',
      value: 'Equal',
      disabled: ['timeHour'].includes(props.compareParam.whenSourceKey ?? '')
    },
    { label: '>', value: 'MoreThan' },
    { label: '>=', value: 'MoreThanOrEqual' }
    // { label: '%', value: 'Residual' }
  ];
});

const getSourceLabel = (sourceKey: string) => {
  return dataSourceWholeItems.find((item) => item.key == sourceKey)?.label;
};

const isWhenValueSourceKey = computed(() =>
  isSourceKeyFormat(props.compareParam.whenValue ?? 0)
);
const whenSourceItem = computed(() => {
  return dataSourceWholeItems.find(
    (item) => item.key == String(props.compareParam.whenSourceKey ?? '')
  );
});

const maxValue = computed(() => {
  const max = whenSourceItem.value?.maxValue ?? 99999;
  // 如果数据源不能达到最大值，则最大值减1
  if (whenSourceItem.value?.canNotReachMaxValue) {
    return max - 1;
  }
  return max;
});

const emits = defineEmits<{
  (event: 'update', id: string, data: Record<string, string | number>): void;
  (event: 'delete', id: string): void;
  (event: 'add', id: string): void;
}>();
const updateAttrs = (id: string, attrs: Record<string, string | number>) => {
  emits('update', id, attrs);
};

// 数据源
const sourceModal = ref();
const sourceEditKey = ref<'whenSourceKey' | 'whenValue'>('whenSourceKey');
const onOpenSource = (key: 'whenSourceKey' | 'whenValue') => {
  if (props.disabled) return;
  sourceEditKey.value = key;
  sourceModal.value?.open?.();
};

const _getSourceDefault = (key: string) => {
  return runtimeStore.configDataDefault[key] ?? 10;
};

const onSourceSelect = (item: DataSourceItem) => {
  const id = props.compareParam.id;
  const sourceKey = item.key;
  // 如果是选择数据源
  if (sourceEditKey.value == 'whenSourceKey' && !isWhenValueSourceKey.value) {
    // 右侧如果是数字，需要修改其值=默认值,没有小数点的使用整数值，确保看起来的数据和实际数据相同（之前工具上看起来是整数，其实是带有小时的值）
    const whenSourceItem = dataSourceWholeItems.find(
      (item) => item.key == String(sourceKey ?? '')
    );
    const sourceDefaultValue = whenSourceItem?.precision ? _getSourceDefault(sourceKey) : Math.floor(_getSourceDefault(sourceKey));
    updateAttrs(id, {
      whenSourceKey: sourceKey,
      whenValue: sourceDefaultValue
    });
  } else {
    updateAttrs(id, { [sourceEditKey.value]: sourceKey });
  }
  // 如果是选择数据源=timeHour
  if (
    sourceEditKey.value == 'whenSourceKey' &&
    sourceKey == 'timeHour' &&
    props.compareParam.whenOperator == 'Equal'
  ) {
    updateAttrs(id, { whenOperator: 'MoreThanOrEqual' });
  }
};

const delSourceKey = () => {
  const value = _getSourceDefault(props.compareParam.whenSourceKey ?? '');
  updateAttrs(props.compareParam.id, { whenValue: value });
};
</script>

<style scoped lang="less">
:deep(.ant-select-arrow) {
  display: none;
}
// :deep(.ant-select-selection-item) {
//   padding-right: 6px !important;
//   padding-left: 4px !important;
// }
:deep(.ant-input-number-handler-wrap) {
  display: none;
}
:deep(.ant-input) {
  padding: 4px !important;
}
:deep(.ant-input-number-input) {
  padding: 0px !important;
  text-align: center !important;
}
:deep(
    .ant-select-single:not(.ant-select-customize-input) .ant-select-selector
  ) {
  padding: 0px 4px !important;
}
._grid {
  display: grid;
  grid-template-columns: 1fr 46px 1fr 48px;
}
:deep(.ant-input-number-group-addon) {
  padding: 0;
  font-size: 0.875rem;
  padding: 2px !important;
}
:deep(.ant-select-selection-item) {
  padding-right: 0 !important;
  text-align: center;
}
:deep(.ant-input, ) {
  // padding-right: 0 !important;
  text-align: center !important;
}
</style>
